import React, { useState } from 'react';
import api from './api'; // Use centralized API service

export const sendMessage = async (message) => {
  try {
    const response = await api.post('/chat', { query: message });
    return response; // New API format - processApiResponse returns data directly
  } catch (error) {
    console.error('Chat error:', error);
    throw error;
  }
};


export const sendMessageStreaming = async function* (message, source = null, source_event = null, talk_mode = false) {
  console.log('🎤 sendMessageStreaming called with:', {
    message: message ? message.substring(0, 50) : null,
    source,
    source_event,
    talk_mode,
    arguments: Array.from(arguments)
  });
  
  try {
    // For streaming, we need to use the raw authRequest from api.js
    const requestBody = { 
      query: message, 
      source: source, 
      event: source_event,
      talk_mode: talk_mode
    };
    
    console.log('🎤 Request body being sent to /chat:', requestBody);
    
    const response = await api.authRequest('/chat', {
      method: 'POST',
      headers: {
        'Accept': 'application/x-ndjson'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('sendMessageStreaming: Unauthorized response, token may be invalid');
      }
      const err = new Error(`HTTP error! status: ${response.status}`);
      err.status = response.status;
      if (response.status === 402) {
        try { err.detail = await response.json(); } catch (_) {}
      }
      throw err;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      
      // Append new data to buffer
      buffer += decoder.decode(value, { stream: true });
      
      // Process complete lines
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        
        if (line) {
          try {
            const parsed = JSON.parse(line);
            yield parsed;
          } catch (err) {
            console.error('Stream chunk parsing error:', err, 'Line:', line.substring(0, 100) + '...');
          }
        }
      }
    }
    
    // Process any remaining data in buffer
    if (buffer.trim()) {
      try {
        const parsed = JSON.parse(buffer.trim());
        yield parsed;
      } catch (err) {
        console.error('Final buffer parsing error:', err, 'Buffer:', buffer.substring(0, 100) + '...');
      }
    }
  } catch (error) {
    console.error('Chat error:', error);
    throw error;
  }
};


export const getChatHistory = async (options = {}) => {
  try {
    // Use centralized API service that handles both old and new response formats
    const response = await api.get('/chat', options);
    return response; // api.js processApiResponse handles format conversion
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error; // Re-throw abort error to be handled by caller
    }
    console.error('Chat history error:', error);
    throw error;
  }
};

/**
 * Send MLS form assistance request directly to My Property endpoint
 * Bypasses supervisor agent for faster, specialized MLS form help
 */
export const sendMLSFormAssistance = async (query, propertyContext = null, fieldName = null, currentValue = null) => {
  try {
    const requestData = {
      query: query,
      property_context: propertyContext,
      field_name: fieldName,
      current_value: currentValue
    };

    // Use centralized API service
    const response = await api.post('/my-property/form-assistance', requestData);
    return response; // api.js processApiResponse handles format conversion
  } catch (error) {
    console.error('MLS form assistance error:', error);
    throw error;
  }
};

/**
 * Upload document temporarily for chat processing
 */
export const uploadDocumentTemporarily = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Use api.js authRequest for file uploads (need special headers handling)
    // Explicitly set Content-Type to null so api.js allows browser to set multipart/form-data boundary
    const response = await api.authRequest('/chat/upload-document-temporarily', {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': null 
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      const detail = errorData.detail;
      let errorMessage = 'Upload failed';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Handle FastAPI validation errors
        errorMessage = detail.map(d => `${d.loc.join('.')}: ${d.msg}`).join(', ');
      } else if (typeof detail === 'object') {
        errorMessage = JSON.stringify(detail);
      }
      
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Document upload error:', error);
    throw error;
  }
};

/**
 * Delete temporarily uploaded document
 */
export const deleteTemporaryDocument = async (filePath) => {
  try {
    const response = await api.delete(`/chat/delete-temporarily-document?file_path=${encodeURIComponent(filePath)}`);
    return response; // New API format - processApiResponse returns data directly
  } catch (error) {
    console.error('Document delete error:', error);
    throw error;
  }
};


export const submitChatFeedback = async ({ thread_id, message_id, feedback_value, reason_code = null, comment = null }) => {
  try {
    const response = await api.post('/chat/feedback', {
      thread_id,
      message_id,
      feedback_value,
      reason_code,
      comment,
    });
    return response;
  } catch (error) {
    console.error('Chat feedback error:', error);
    throw error;
  }
};